import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { Clock } from "lucide-react";
import { noop } from 'lodash';
import React, { useRef, useState } from "react";
import pickerTheme, { pickerPopperProps } from "common/pickerTheme";

// Mirrors DateSelection so a date and a time can be asked for separately.
const useOutlinedInputStyles = makeStyles(theme => ({
  root: ({ showBorder }) => ({
    backgroundColor: 'white',
    borderRadius: 6,
    boxShadow: showBorder
      ? '0 0 0 1px rgba(0, 0, 0, 0.1)'
      : '0px 0px 2px rgba(0, 0, 0, 0.1) inset',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
      borderColor: showBorder ? '#999' : 'transparent',
    },
  }),
  notchedOutline: ({ showBorder }) => ({
    border: showBorder ? '1px solid #ddd !important' : '0px !important',
  }),
  disabled: {},
  focused: {},
  error: {},
}));

const TimeSelection = ({
  onAnswered = noop(),
  value = null,
  disabled = false,
  showBorder = false,
}) => {
  const classes = useOutlinedInputStyles({ showBorder });
  const [open, setOpen] = useState(false);
  const [redraw, setRedraw] = useState(0);
  const halfTyped = useRef(false);

  // Half-typed text parses to an invalid time. Sending it up would rewrite
  // `value` and snap the field back to the old time mid-edit, so it is held
  // back. null still goes through, which is how the field gets cleared.
  const handleChange = (time, text) => {
    const usable = !time || time.isValid();
    halfTyped.current = Boolean(text) && !usable;
    if (usable) onAnswered(time);
  };

  // Text the form never saw has to be dropped once the edit ends; the picker
  // only redraws from `value` on a remount.
  const endEdit = () => {
    if (!halfTyped.current) return;
    halfTyped.current = false;
    setRedraw(n => n + 1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={pickerTheme}>
          <DesktopTimePicker
            key={redraw}
            inputFormat="hh:mm A"
            showToolbar
            ampmInClock={false}
            disableMaskedInput
            PopperProps={pickerPopperProps}
            value={value}
            disabled={disabled}
            onChange={handleChange}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => {
              setOpen(false);
              endEdit();
            }}
            minutesStep={5}
            components={{ OpenPickerIcon: () => <Clock size={18} strokeWidth={2}/> }}
            OpenPickerButtonProps={{ 'aria-label': 'Select Time' }}
            renderInput={params => (
              <TextField
                {...params}
                id="time-picker"
                fullWidth
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  placeholder: "hh:mm AM",
                  onClick: () => setOpen(true),
                  // An open popper means the edit is still running, and its own
                  // close handler ends it.
                  onBlur: () => !open && endEdit(),
                }}
                InputProps={{
                  ...params.InputProps,
                  classes: {root: classes.root, notchedOutline: classes.notchedOutline},
                }}
              />
            )}
          />
        </ThemeProvider>
      </StyledEngineProvider>
    </LocalizationProvider>
  );
};

export default TimeSelection;
